package dev.danko.roblox_craft_ai.uikit.components

import com.varabyte.kobweb.compose.css.WhiteSpace
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.ms
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s

/** Three levels (style A): PRIMARY — one per screen; SECONDARY/OUTLINE — ordinary; GHOST — quiet actions. */
enum class AppButtonVariant {
    PRIMARY,
    SECONDARY,
    OUTLINE,
    GHOST,
    DANGER
}

enum class AppButtonSize {
    SM,
    MD,
    LG
}

val AppButtonSpinKeyframes = Keyframes {
    from {
        Modifier.rotate(0.deg)
    }
    to {
        Modifier.rotate(360.deg)
    }
}

val AppButtonBaseStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .userSelect(UserSelect.None)
            .cursor(Cursor.Pointer)
            .fontWeight(FontWeight.SemiBold)
            .whiteSpace(WhiteSpace.NoWrap)
            .flexShrink(0)
            .transition(
                Transition.of("background-color", 150.ms),
                Transition.of("border-color", 150.ms),
                Transition.of("color", 150.ms),
                Transition.of("box-shadow", 150.ms),
                Transition.of("transform", 100.ms)
            )
    }
    cssRule(":active") {
        Modifier.scale(0.97)
    }
    cssRule(":focus-visible") {
        Modifier.outline(2.px, LineStyle.Solid, Color.rgb(0xB9A3FF)).outlineOffset(2.px)
    }
}

val PrimaryButtonStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(palette.accent)
            .border(0.px, LineStyle.None, Colors.Transparent)
            .color(Colors.White)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(palette.accentGlow)
            .color(Colors.White)
    }
}

val SecondaryButtonStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(palette.surfaceElevated)
            .border(1.px, LineStyle.Solid, palette.borderStrong)
            .color(palette.textPrimary)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(palette.border)
            .border(1.px, LineStyle.Solid, palette.borderStrong)
            .color(palette.textPrimary)
    }
}

val OutlineButtonStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(Colors.Transparent)
            .border(1.px, LineStyle.Solid, palette.borderStrong)
            .color(palette.textPrimary)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(palette.surfaceElevated)
            .border(1.px, LineStyle.Solid, palette.borderStrong)
            .color(palette.textPrimary)
    }
}

val GhostButtonStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(Colors.Transparent)
            .border(1.px, LineStyle.Solid, Colors.Transparent)
            .color(palette.textSecondary)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(palette.surfaceElevated)
            .color(palette.textPrimary)
    }
}

val DangerButtonStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .backgroundColor(palette.statusFailed)
            .border(0.px, LineStyle.None, Colors.Transparent)
            .color(Colors.White)
    }
    cssRule(":hover") {
        Modifier
            .backgroundColor(Color.rgb(0xC93636))
            .color(Colors.White)
    }
}

/** SM 36px for dense toolbars, MD 44px (comfortable touch target), LG 48px for hero actions. */
fun selectSizeModifier(size: AppButtonSize): Modifier = when (size) {
    AppButtonSize.SM -> Modifier
        .padding(topBottom = 0.cssRem, leftRight = 0.75.cssRem)
        .fontSize(0.8125.cssRem)
        .borderRadius(9.px)
        .minHeight(36.px)
        .gap(0.375.cssRem)
    AppButtonSize.MD -> Modifier
        .padding(topBottom = 0.cssRem, leftRight = 1.cssRem)
        .fontSize(0.9375.cssRem)
        .borderRadius(10.px)
        .minHeight(44.px)
        .gap(0.5.cssRem)
    AppButtonSize.LG -> Modifier
        .padding(topBottom = 0.cssRem, leftRight = 1.375.cssRem)
        .fontSize(1.cssRem)
        .borderRadius(12.px)
        .minHeight(48.px)
        .gap(0.5.cssRem)
}

@Composable
fun selectVariantModifier(variant: AppButtonVariant): Modifier = when (variant) {
    AppButtonVariant.PRIMARY -> PrimaryButtonStyle.toModifier()
    AppButtonVariant.SECONDARY -> SecondaryButtonStyle.toModifier()
    AppButtonVariant.OUTLINE -> OutlineButtonStyle.toModifier()
    AppButtonVariant.GHOST -> GhostButtonStyle.toModifier()
    AppButtonVariant.DANGER -> DangerButtonStyle.toModifier()
}

@Composable
fun AppButton(
    text: String,
    onClick: () -> Unit,
    variant: AppButtonVariant = AppButtonVariant.PRIMARY,
    size: AppButtonSize = AppButtonSize.MD,
    enabled: Boolean = true,
    loading: Boolean = false,
    iconLeft: String? = null,
    iconRight: String? = null,
    fullWidth: Boolean = false,
    modifier: Modifier = Modifier,
    icon: AppIcon? = null,
    ariaLabel: String? = null
) {
    val isInteractive = enabled && !loading

    val interactiveModifier = if (!isInteractive) {
        Modifier
            .opacity(0.5)
            .cursor(Cursor.NotAllowed)
    } else {
        Modifier
    }

    val widthModifier = if (fullWidth) Modifier.fillMaxWidth() else Modifier

    val combinedModifier = AppButtonBaseStyle.toModifier()
        .then(selectVariantModifier(variant))
        .then(selectSizeModifier(size))
        .then(interactiveModifier)
        .then(widthModifier)
        .then(modifier)
        .onClick {
            if (isInteractive) {
                onClick()
            }
        }

    org.jetbrains.compose.web.dom.Button(
        attrs = combinedModifier.toAttrs {
            attr("role", "button")
            attr("tabindex", if (isInteractive) "0" else "-1")
            attr("type", "button")
            (ariaLabel ?: text.takeIf { it.isEmpty() && icon != null }?.let { iconRight ?: iconLeft })?.let { attr("aria-label", it) }
        }
    ) {
        if (loading) {
            SpanText(
                "↻",
                Modifier.animation(
                    AppButtonSpinKeyframes.toAnimation(
                        duration = 1.s,
                        iterationCount = AnimationIterationCount.Infinite
                    )
                )
            )
        } else if (icon != null) {
            Icon(icon, size = if (size == AppButtonSize.SM) 16 else 18)
        } else if (iconLeft != null) {
            SpanText(iconLeft)
        }

        if (text.isNotEmpty()) {
            SpanText(text)
        }

        if (!loading && iconRight != null) {
            SpanText(iconRight)
        }
    }
}
